import { API_BASE_URL } from '../config/api';

import { Track, CreateTrackRequest, UpdateTrackRequest, CreateTrackReportRequest } from '../types/artist';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export const trackApi = {
  async getAllTracks(): Promise<Track[]> {
    try {
      console.log('Getting all tracks');
      console.log('API URL:', `${API_BASE_URL}/api/Track`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Track`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Get tracks response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Get tracks response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Get tracks failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to get tracks');
      }

      return data;
    } catch (error) {
      console.error('Get tracks error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async getTrackById(id: number): Promise<Track> {
    try {
      console.log('Getting track by ID:', id);
      console.log('API URL:', `${API_BASE_URL}/api/Track/${id}`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Track/${id}`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Get track response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Get track response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Get track failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to get track');
      }

      return data;
    } catch (error) {
      console.error('Get track error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async getTrackByPublicId(publicId: string): Promise<Track> {
    try {
      console.log('Getting track by public ID:', publicId);
      console.log('API URL:', `${API_BASE_URL}/api/Track/public/${publicId}`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Track/public/${publicId}`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Get track by public ID response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Get track by public ID response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Get track by public ID failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to get track by public ID');
      }

      return data;
    } catch (error) {
      console.error('Get track by public ID error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async getTracksByUserId(userId: string): Promise<Track[]> {
    try {
      console.log('Getting tracks by user ID:', userId);
      console.log('API URL:', `${API_BASE_URL}/api/Track/user/${userId}`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Track/user/${userId}`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Get tracks by user response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Get tracks by user response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Get tracks by user failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to get tracks by user');
      }

      return data;
    } catch (error) {
      console.error('Get tracks by user error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async createTrack(trackData: CreateTrackRequest): Promise<Track> {
    try {
      console.log('Creating track with data:', trackData);
      console.log('API URL:', `${API_BASE_URL}/api/Track`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Track`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(trackData),
      });

      console.log('Create track response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Create track response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Create track failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to create track');
      }

      return data;
    } catch (error) {
      console.error('Create track error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  // Create track with audio file (atomic operation)
  async createTrackWithAudio(trackData: CreateTrackRequest, audioFile: File): Promise<Track> {
    try {
      console.log('Creating track with audio file:', trackData);
      console.log('API URL:', `${API_BASE_URL}/api/Track`);
      
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      
      // Add track data fields to FormData
      formData.append('title', trackData.title);
      formData.append('artistId', trackData.artistId.toString());
      if (trackData.genre) {
        formData.append('genre', trackData.genre);
      }
      if (trackData.producerNotes) {
        formData.append('producerNotes', trackData.producerNotes);
      }
      
      // Add audio file
      formData.append('audioFile', audioFile);

      const response = await fetch(`${API_BASE_URL}/api/Track`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type header for FormData - browser will set it with boundary
        },
        body: formData,
      });

      console.log('Create track with audio response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Create track with audio response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Create track with audio failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to create track with audio');
      }

      return data;
    } catch (error) {
      console.error('Create track with audio error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async updateTrack(id: number, trackData: UpdateTrackRequest): Promise<void> {
    try {
      console.log('Updating track with data:', trackData);
      console.log('API URL:', `${API_BASE_URL}/api/Track/${id}`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Track/${id}`, {
        method: 'PUT',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(trackData),
      });

      console.log('Update track response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to update track';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Update track failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Update track error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async deleteTrack(id: number): Promise<void> {
    try {
      console.log('Deleting track with ID:', id);
      console.log('API URL:', `${API_BASE_URL}/api/Track/${id}`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Track/${id}`, {
        method: 'DELETE',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Delete track response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to delete track';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Delete track failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Delete track error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  // Upload audio file for a track
  async uploadTrackFile(file: File, trackId: number): Promise<void> {
    try {
      console.log('Uploading file for track ID:', trackId);
      
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('audioFile', file);

      const response = await fetch(`${API_BASE_URL}/api/Track/${trackId}/upload`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type header for FormData - browser will set it with boundary
        },
        body: formData,
      });

      console.log('Upload response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to upload track file';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Upload failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  // Get audio stream URL for a track
  getAudioStreamUrl(trackId: number): string {
    return `${API_BASE_URL}/api/Track/${trackId}/audio`;
  },

  // Get top tracks for feedback
  async getTopTracks(count: number = 10, genres?: string[]): Promise<Track[]> {
    try {
      console.log('Getting top tracks, count:', count, 'genres:', genres);
      
      // Build URL with query parameters
      let url = `${API_BASE_URL}/api/Track/toptracks/${count}`;
      
      // Add genre parameters if provided
      if (genres && genres.length > 0) {
        const params = new URLSearchParams();
        genres.forEach(genre => {
          params.append('genres', genre);
        });
        url += `?${params.toString()}`;
      }
      
      console.log('API URL:', url);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Get top tracks response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Get top tracks response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Get top tracks failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to get top tracks');
      }

      return data;
    } catch (error) {
      console.error('Get top tracks error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  // Get reviewed tracks (paginated)
  async getReviewedTracks(page: number = 1, pageSize: number = 10): Promise<any> {
    try {
      console.log('Getting reviewed tracks, page:', page, 'pageSize:', pageSize);
      
      // Build URL with query parameters
      const params = new URLSearchParams();
      params.append('Page', page.toString());
      params.append('PageSize', pageSize.toString());
      
      const url = `${API_BASE_URL}/api/Track/reviewed?${params.toString()}`;
      console.log('API URL:', url);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Get reviewed tracks response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Get reviewed tracks response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Get reviewed tracks failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to get reviewed tracks');
      }

      return data;
    } catch (error) {
      console.error('Get reviewed tracks error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  // Report a track for content violation
  async reportTrack(publicId: string, reportData: CreateTrackReportRequest): Promise<void> {
    try {
      console.log('Reporting track with public ID:', publicId);
      console.log('API URL:', `${API_BASE_URL}/api/Track/public/${publicId}/report`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Track/public/${publicId}/report`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reportData),
      });

      console.log('Report track response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to report track';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Report track failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Report track error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },
};